<template>
  <div class="tw-z-[1000]">
    <o-tooltip
      variant="primary"
      position="top"
      :active="tooltipActive"
      :label="tooltipLabel"
      class="tw-flex-grow"
      multiline
    >
      <button
        class="tw-text-lg tw-bg-[#F1583D] tw-text-white tw-rounded-lg
      tw-px-8 tw-py-4 tw-border-none tw-font-inter tw-font-semibold tw-cursor-pointer"
        :class="{ 'tw-cursor-not-allowed': !checkupStore.portfolios.length }"
        :disabled="!checkupStore.portfolios.length"
        @click="submit"
      >
        <span v-if="spinnerIsLoading">
          <i class="fa fa-spinner fa-spin tw-text-xl tw-mr-1" />
          Getting analytics
        </span>
        <span v-else>Run Analysis</span>
      </button>
    </o-tooltip>

    <b-notification
      v-if="checkupStore.activeNotifications.ANALYTICS_FAILED && !checkupStore.activeNotifications.ANALYTICS_FAILED_0_DOLLARS"
      type="is-danger"
    >
      <div class="tw-flex tw-items-center tw-gap-3">
        <p class="tw-max-w-sm">
          Something went wrong while trying to run this analysis. The YourStake Team has been notified and will correct and complete the upload and notify you within one business day.
        </p>
      </div>
    </b-notification>

    <b-notification
      v-if="checkupStore.activeNotifications.ANALYTICS_FAILED_0_DOLLARS"
      type="is-danger"
    >
      <div class="tw-flex tw-items-center tw-gap-3">
        <p class="tw-max-w-sm">
          There are no matched holdings with more than 0 dollars in this portfolio. Please update holdings in the Holdings tab.
        </p>
      </div>
    </b-notification>
  </div>
</template>


<script>
// import SubComponent from '../components/SubComponent'
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';
import * as Sentry from '@sentry/vue';
import optResultMock from './opt-result-mock.json';
import analysisResultsMock from './analysis-results-mock.json';

export default {
  components: { /* Subcomponents */ },
  props: {
    // prop: { type: String, required: true },
    // prop: String,
  },
  data() {
    return {
      totalAccounts: 0,
      currentAccountIndex: 0,
      optimizationIsRunning: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    tooltipActive() {
      return !this.checkupStore.portfolios.length;
    },
    tooltipLabel() {
      return 'Cannot run analysis without a benchmark and at least one portfolio';
    },
    headers() {
      return {
        'X-CSRFToken': this.$root.$data.csrfToken,
        'Content-Type': 'application/json',
      };
    },
    spinnerIsLoading() {
      return this.checkupStore.isLoading;
    },
  },
  beforeCreate() {},
  created() {},
  mounted() {},
  updated() {},
  methods: {
    async mergePortfolios(ids, merge_type = 'portfolio') {
      const equity_only = this.checkupStore.selectedPortfolios.length > 0 && this.checkupStore.selectedPortfolios.every(portfolio => portfolio.equity_only_id);

      // const portfolios = (merge_type === 'portfolio' ? this.checkupStore.selectedPortfolios : this.checkupStore.selectedBenchmarks)
      // .map(portfolio => ({
      //   id: portfolio.id,
      //   taxabilityStatus: portfolio.taxabilityStatus || null,  // Pass null if taxabilityStatus is not set
      // }));

      if (!ids.length) throw new Error('No portfolios selected');
      console.log('Trying to merge portfolios', ids);

      // Make a post request to the merge portfolios route
      const url = '/api/v1/merge-portfolios/';
      const body = { portfolio_ids: ids, equity_only };  // Pass the portfolios with taxabilityStatus
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRFToken': this.$root.$data.csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      console.log('MERGE COMPLETED', data);
      return { ...data.merged_portfolio, isChecked: true };
    },
    async processPortfolio(portfolio) {
      console.log(portfolio.dollars);
      const extID = portfolio.external_id || portfolio.id;
      const portfolioAnalysis = await this.getAnalysisResults(extID, portfolio.label);

      const withAnalytics = {
        ...portfolio,
        ...portfolioAnalysis,
      };

      if (extID === 'default_benchmark') {
        this.checkupStore.setBenchmark(withAnalytics);
      } else {
        this.checkupStore.updatePortfolio(withAnalytics);
      }
    },

    async processPortfolios(portfoliosToProcess, update_checkup = false) {
      console.log('Portfolios to process:', portfoliosToProcess);
      // Calculate the total dollars of all portfolios
      const totalDollars = portfoliosToProcess.reduce((sum, portfolio) => {
        let dollars;
        if (portfolio?.total_dollars || portfolio?.total_dollars === 0) {
          // Use total_dollars if it exists
          dollars = parseFloat(portfolio.total_dollars) || 0;
        } else if (portfolio?.uploaded_df_agg && Array.isArray(portfolio.uploaded_df_agg)) {
          // If total_dollars doesn't exist, sum all the dollars in uploaded_df
          dollars = portfolio.uploaded_df_agg.reduce((dfSum, entry) => {
            return dfSum + (parseFloat(entry.dollars) || 0); // Sum dollars from each entry
          }, 0);
        } else {
          dollars = 1;
        }

        return sum + dollars;
      }, 0);

      if (Math.round(totalDollars * 100) / 100 === 0) { // Use tolerance for floating-point comparison
        // Display warning if the total dollars for all portfolios is 0
        const error = 'Portfolio value is 0 dollars so not processed.';
        console.log('error getting analytics', error);
        this.analyticsFailed = true;
        this.checkupStore.addNotification('ANALYTICS_FAILED_0_DOLLARS', { error });
        this.checkupStore.setLoadingState('portfolio_loading', false);
      } else {
        // Proceed with processing each portfolio if total dollars is not 0
        await Promise.all(portfoliosToProcess.map(async portfolio => {
          if (portfolio?.total_dollars > 0 || portfolio?.total_dollars === undefined) {
            await this.processPortfolio(portfolio); // Only process if dollars > 0
          } else {
            // Log if individual portfolio has 0 dollars, but don't fail overall analytics
            const error = `Portfolio ${portfolio.label} has 0 dollars and was not processed.`;
            console.log('Error in analytics:', error);
          }
        }));
      }
      await this.checkupStore.saveCheckup(update_checkup);
      // Now we have all the analytics results for all the portfolios
      this.$emit('analytics-done');
    },

    async getAnalysisResults(portfolioID, label) {
      // Gets the portfolio analysis results from the backend
      if (!portfolioID) return;
      this.analyticsFailed = false;

      const loadingStateKey = `is${label.charAt(0).toUpperCase() + label.slice(1)}Loading`;

      const base = `/api/v1/portfolios/${portfolioID}/analytics/`;
      const ids = this.checkupStore.selectedValueIds.join(',');
      const params = {};
      if (ids) {
        params['values'] = ids;
      }
      if (this.checkupStore.optimization) {
        params['equityOnly'] = true;
      }
      const queryString = (new URLSearchParams(params)).toString();
      const url = `${base}?${queryString}`;

      try {
        if (loadingStateKey) {
          this.checkupStore.setLoadingState(loadingStateKey, true);
          this.checkupStore.setLoadingState('isAreasLoading', false);
        }
        const pre_analytics_time = new Date().getTime();
        const pingInterval = 500;
        const data = await awaitTask(url, { method: 'GET', headers: this.headers }, pingInterval);
        const end_time = new Date().getTime();
        console.log(new Date().toISOString(), 'Analytics completed for', portfolioID);
        console.log('time elapsed in analytics', end_time - pre_analytics_time, 'ms');
        if (data?.task?.meta?.error) {
          this.analyticsFailed = true;
          this.checkupStore.addNotification('ANALYTICS_FAILED_0_DOLLARS', { error: data.task.meta.error });
          if (loadingStateKey) {
            this.checkupStore.setLoadingState(loadingStateKey, false);
          }
          const error = `Error in analytics: ${data.task.meta.error}`;
          throw error;
        }


        this.updateOptimizationsAfterAnalytics(data.task);

        if (loadingStateKey) {
          this.checkupStore.setLoadingState(loadingStateKey, false);
        }
        return { ...data.task.meta.result, id: data.task.meta.result.external_id };
      } catch (error) {
        console.error('Error getting analytics results');
        console.error(error);
        this.analyticsFailed = true;
        this.checkupStore.addNotification('ANALYTICS_FAILED', { error });
        if (loadingStateKey) {
          this.checkupStore.setLoadingState(loadingStateKey, false);
        }
        throw error;
      }
    },

    updateOptimizationsAfterAnalytics(task) {
      const latest = task.meta.result?.latest_optimization;
      if (!latest) return;

      const optimizations = [...(this.checkupStore.optimization?.optimizations || [])];

      if (!optimizations.some(opt => opt.external_id === latest.external_id)) {
        optimizations.push(latest);
      }

      this.checkupStore.optimization = {
        ...this.checkupStore.optimization,
        optimizations,
      };
    },

    async submit() {
      if (!this.validateOverrides()) {
        alert('You must select a symbol, min, and max for each override.');
        return;
      }

      this.checkupStore.setLoadingState('overall', true);
      // merge portfolios with label selectedPortfolio
      const ids = this.checkupStore.selectedPortfolios?.map(portfolio => portfolio.external_id);
      let combinedPortfolio = this.checkupStore.selectedPortfolios?.[0];
      if (ids.length > 1) {
        combinedPortfolio = await this.mergePortfolios(ids);
      }
      if (combinedPortfolio) {
        this.checkupStore.setCombinedPortfolio(combinedPortfolio);
      }

      console.log('PORTFOLIOS TO PROCESS', [this.checkupStore.portfolio, this.checkupStore.benchmark, this.checkupStore.optimizedPortfolio]);
      let update_checkup = false;
      if (this.checkupStore.optimizerChecked && this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization')) {
        console.log('OPTIMIZER CHECKED Creating Blank Checkup');
        await this.checkupStore.saveCheckup();
        update_checkup = true;
      }
      const analyticsPromise = this.processPortfolios([this.checkupStore.portfolio, this.checkupStore.benchmark, this.checkupStore.optimizedPortfolio].filter(Boolean), update_checkup);
      this.checkupStore.setLoadingState('overall', false);

      let optimizationPromise = Promise.resolve();
      if (this.checkupStore.optimizerChecked && this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization')) {
        console.log('OPTIMIZER CHECKED running optimization');
        optimizationPromise = this.runOptimization();
      }

      await Promise.all([analyticsPromise, optimizationPromise]);
    },

    async runOptimization() {
      if (!this.validateOverrides()) {
        alert('You must select a symbol, min, and max for each override.');
        return;
      }
      this.totalAccounts = this.checkupStore.accounts?.length || 1;
      this.currentAccountIndex = 0;
      this.checkupStore.isOptimizationLoading = true;
      // Reset lastOptimizerInputs so the per-account cache dedup check doesn't
      // suppress subsequent accounts when they share the same base config.
      this.checkupStore.lastOptimizerInputs = null;
      console.log('COMPUTING ALL', this.checkupStore.accounts);
      try {
        const optimizations = [];

        if (!this.checkupStore.accounts?.length) {
          this.currentAccountIndex += 1;
          console.log('COMPUTING WITHOUT ACCOUNT');
          const optimization = await this.checkupStore.computeVadis(null);
          // const optimization = optResultMock; // Use mock for single optimization
          optimizations.push(optimization);
        } else {
          for (const account of this.checkupStore.accounts) {
            this.currentAccountIndex += 1;
            console.log('COMPUTING ACCOUNT:', account);
            const optimization = await this.checkupStore.computeVadis(account);
            // const optimization = optResultMock; // Use the same mock for each account
            optimizations.push(optimization);
          }
        }
        console.log('OPTIMIZATIONS HERE', optimizations);
        // Filter out null entries (cache hits that returned no new result)
        const validOptimizations = optimizations.filter(Boolean);
        // Merge optimized portfolios if more than one exists
        const optimizedPortfolioIds = validOptimizations.map(opt => opt.optimization_portfolio_entity_external_id || opt.external_id);

        let mergedOptimizedPortfolio = null;
        if (optimizedPortfolioIds.length > 1) {
          mergedOptimizedPortfolio = await this.mergePortfolios(optimizedPortfolioIds);
        } else {
          mergedOptimizedPortfolio = validOptimizations[0]?.optimization_portfolio_entity;
        }

        // Run analysis on merged optimized portfolio
        if (mergedOptimizedPortfolio) {
          const extID = mergedOptimizedPortfolio.external_id;
          console.error('EXT ID HERE', extID);
          const portfolioAnalysis = await this.getAnalysisResults(extID, 'optimizedPortfolio');
          // const portfolioAnalysis = analysisResultsMock; // Use mock for analysis results
          console.log('Merged Optimized Portfolio Analysis Results', portfolioAnalysis);

          // Update the checkupStore with the merged and analyzed optimized portfolio
          this.checkupStore.selectOptimizedPortfolio({ ...mergedOptimizedPortfolio, ...portfolioAnalysis });
        }

        // Set risk-based benchmark if available
        if (validOptimizations[0]?.risk_based_benchmark) {
          this.checkupStore.riskBasedBenchmark = validOptimizations[0].risk_based_benchmark;
        }

        this.checkupStore.isOptimizationLoading = false;
        console.log('OPTIMIZATION AND ANALYSIS DONE');
        const update_checkup = true;
        await this.checkupStore.saveCheckup(update_checkup);
        this.$emit('analytics-done');
      } catch (err) {
        console.error('Optimization or analysis failed', err);
        this.checkupStore.addNotification('OPTIMIZATION_FAILED', { error: err });
        this.checkupStore.isOptimizationLoading = false;
      }
    },

    validateOverrides() {
      return ! this.checkupStore.overrides.some(override => {
        return (override.symbol == '' || override.symbol == null || ! Number.isFinite(override.minWeight) || ! Number.isFinite(override.maxWeight) );
      });
    },
  },
};
</script>


  <style scoped>

  </style>
